import { defineStore } from "pinia";
import { computed, shallowRef } from "vue";
import { dispatchInto } from "../lib/tea";
import {
  initialEditorChromeModel,
  updateEditorChrome,
  type EditorChromeActions,
  type EditorChromeMsg,
} from "./useEditorChromeStore.update";

export type { EditorChromeActions };

/**
 * Status-bar / native-menu chrome actions for the editor surface.
 * DocumentEditor registers the real implementations; EditorShell may install
 * stubs first so the status bar can bind before the editor mounts.
 *
 * TEA: pure Model/Msg in `useEditorChromeStore.update.ts`. The exposed
 * action functions are stable wrappers that call whatever handler is in the
 * Model at call time, so callers may bind them before `register` runs.
 */
export const useEditorChromeStore = defineStore("editorChrome", () => {
  const model = shallowRef(initialEditorChromeModel);

  function send(msg: EditorChromeMsg): void {
    void dispatchInto(model, msg, updateEditorChrome, () => {});
  }

  const registered = computed(() => model.value.registered);

  function toggleHistory(): void {
    model.value.handlers.toggleHistory();
  }
  function toggleComments(): void {
    model.value.handlers.toggleComments();
  }
  function closeHistory(): void {
    model.value.handlers.closeHistory();
  }
  function openFind(): void {
    model.value.handlers.openFind();
  }
  function findNext(): void {
    model.value.handlers.findNext();
  }
  function selectNextOccurrence(): void {
    model.value.handlers.selectNextOccurrence();
  }
  function openThesaurus(): void {
    model.value.handlers.openThesaurus();
  }

  function register(actions: EditorChromeActions): void {
    send({ type: "Register", actions });
  }

  return {
    registered,
    toggleHistory,
    toggleComments,
    closeHistory,
    openFind,
    findNext,
    selectNextOccurrence,
    openThesaurus,
    register,
  };
});
